import IconExpandMore from '@iconify/icons-material-symbols/expand-more-sharp';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import type { PointerEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router';
import { Icon, type IconProps } from '#pie/components/icon/Icon';
import type { AuthorizationRoles, MarketRole } from '#pie/internal/api/pie-bff';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { tv } from '#pie/utils/TailwindUtils';

export interface Menu {
  exactMatch?: boolean;
  to: string;
  path?: string;
  label: string;
  description?: string;
  icon: IconProps['icon'];
  children?: { to: string; label: string }[];
  allowedRoles?: AuthorizationRoles[];
  allowedMarketRoles?: MarketRole[];
  adornment?: (isOpen?: boolean) => ReactNode;
}

interface SidebarMenuProps extends VariantProps<typeof styles> {
  menu: Menu[];
  className?: string;
}

export const SidebarMenu = ({ menu, isOpen, className }: SidebarMenuProps) => {
  const s = styles({ isOpen });
  const [value, setValue] = useState<string>();
  const { pathname } = useLocation();

  useEffect(() => {
    if (isOpen) {
      menu.forEach((item) => {
        if (matchPath({ end: false, path: item.to }, pathname)) {
          setValue(item.to);
        }
      });
    }
  }, [isOpen, menu, pathname]);

  useEffect(() => {
    if (!isOpen) {
      setValue(undefined);
    }
  }, [isOpen]);

  const handlePrevent = (event: Event | PointerEvent) => {
    if (isOpen) {
      event.preventDefault();
    }
  };

  return (
    <NavigationMenu.Root
      value={value}
      onValueChange={setValue}
      orientation={isOpen ? 'horizontal' : 'vertical'}
      className={s.base({ className })}
    >
      <NavigationMenu.List>
        {menu.map((item, idx) => {
          const isActive = !!matchPath(
            { path: (item.path ?? item.to) + '/*' },
            pathname,
          );

          // Render dropdown item when item has children
          if (item.children) {
            return (
              <NavigationMenu.Item
                key={idx}
                value={item.to}
                className="relative"
              >
                <NavigationMenu.Trigger
                  onPointerMove={handlePrevent}
                  onPointerLeave={handlePrevent}
                  asChild
                >
                  <button
                    type="button"
                    className={s.item({ isActive })}
                    aria-label={item.label}
                  >
                    <Icon icon={item.icon} />
                    {isOpen && item.label}
                    {isOpen && (
                      <Icon
                        icon={IconExpandMore}
                        className="ml-auto text-content-reversed"
                        size={16}
                      />
                    )}
                  </button>
                </NavigationMenu.Trigger>

                <NavigationMenu.Content
                  onPointerMove={handlePrevent}
                  onPointerLeave={handlePrevent}
                  onPointerDownOutside={handlePrevent}
                  onEscapeKeyDown={handlePrevent}
                  onFocusOutside={handlePrevent}
                  onInteractOutside={handlePrevent}
                  className={s.dropdown()}
                >
                  <NavigationMenu.Sub>
                    <NavigationMenu.List>
                      {item.children.map((child, idx) => {
                        const isActive = !!matchPath(
                          { path: child.to + '/*' },
                          pathname,
                        );

                        return (
                          <NavigationMenu.Item key={idx}>
                            <NavigationMenu.Link asChild>
                              <Link
                                to={child.to}
                                className={s.dropdownItem({ isActive })}
                              >
                                {child.label}
                              </Link>
                            </NavigationMenu.Link>
                          </NavigationMenu.Item>
                        );
                      })}
                    </NavigationMenu.List>

                    <NavigationMenu.Viewport />
                  </NavigationMenu.Sub>
                </NavigationMenu.Content>
              </NavigationMenu.Item>
            );
          }

          // Render simple item when item has no children
          return (
            <NavigationMenu.Item key={idx} value={item.to} className="relative">
              <NavigationMenu.Link asChild>
                <Link
                  to={item.to}
                  className={s.item({ isActive })}
                  aria-label={item.label}
                >
                  <Icon icon={item.icon} className="shrink-0" />
                  {isOpen && item.label}
                  {!!item.adornment && item.adornment(isOpen)}
                </Link>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          );
        })}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
};

export const styles = tv({
  slots: {
    adornment: '',
    base: 'flex flex-col gap-3 mt-6',
    dropdown: '',
    dropdownItem:
      'flex text-sm hover:underline focus:underline focus:outline-hidden',
    item: 'text-left flex gap-3 w-full items-center text-paragraph text-content-reversed py-3 hover:bg-action-primary-hover transition-colors focus:outline-hidden px-5',
  },

  variants: {
    isActive: {
      false: {
        item: '',
      },

      true: {
        dropdownItem: 'underline',
        item: 'bg-action-primary-active',
      },
    },

    isOpen: {
      false: {
        adornment: 'w-3 h-3 absolute top-0 right-3',
        dropdown: 'absolute left-full top-0 w-56 bg-white',
        dropdownItem: 'whitespace-nowrap px-7 py-3',
        item: 'justify-center',
      },
      true: {
        adornment: '',
        dropdown: 'my-3',
        dropdownItem: 'pl-7 text-content-reversed py-3 ml-8',
        item: '',
      },
    },
  },
});
